import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Clock, Crown, MessageSquare, MoreHorizontal, Plus, UserCheck, UserPlus, X, type LucideIcon } from 'lucide-react';
import { Permission, type PublicUser, type UserProfile } from '@spawnwire/shared';
import { Avatar, Button, clsx, DropdownMenu, IconButton, ProfileBanner, Spinner, statusLabel, Tooltip, UserBadge } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { LinkifiedText } from '../../lib/markdown';
import { NameText } from '../../lib/nameFonts';
import { attempt, openDm } from '../../lib/data';
import { useIsMobile } from '../../lib/hooks';
import { can, myTopRolePosition, spacePermissions } from '../../lib/perms';
import { relativeFuture } from '../../lib/time';
import { useStore } from '../../store/store';
import { ui, type ProfileAnchor } from '../../store/ui';
import { SpaceIcon } from '../shell/SpaceIcon';
import { SpaceTagBadge } from './SpaceTag';
import { userMenu } from './userMenu';

/**
 * - `dialog`: centred window with full-size buttons.
 * - `popout`: compact card beside the avatar or name that was clicked.
 * - `panel`: compact card filling the side panel of a one-to-one conversation.
 */
type Variant = 'dialog' | 'popout' | 'panel';

/** Small round button that sits on the banner in the compact layouts. */
function BannerAction({ icon: Icon, label, onClick, disabled }: { icon: LucideIcon; label: string; onClick?: () => void; disabled?: boolean }) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className="flex size-8 items-center justify-center rounded-full bg-black/45 text-white transition-colors hover:bg-black/65 disabled:cursor-default disabled:hover:bg-black/45"
      >
        <Icon className="size-4" aria-hidden />
      </button>
    </Tooltip>
  );
}

/** Display name with their space tag (and a crown for the space owner), then @username and pronouns. */
export function ProfileName({ user, name, owner }: { user: PublicUser; name?: string | null; owner?: boolean }) {
  return (
    <>
      <div className="flex items-center gap-2">
        <h2 className="min-w-0 text-xl font-bold text-fg [overflow-wrap:anywhere]">
          <NameText font={user.nameFont}>{name || user.displayName}</NameText>
        </h2>
        <UserBadge user={user} className="ml-0" />
        {user.tag && <SpaceTagBadge tag={user.tag} />}
        {owner && <Crown className="size-4 shrink-0 text-warning" aria-label="Space owner" />}
      </div>
      <p className="text-sm text-muted [overflow-wrap:anywhere]">
        @{user.username}
        {user.pronouns ? ` · ${user.pronouns}` : ''}
      </p>
    </>
  );
}

/** The bio. With `clamp` it is cut to a few lines, with View Full Bio to show the rest. */
export function AboutMe({ bio, clamp, className }: { bio: string; clamp?: boolean; className?: string }) {
  const [full, setFull] = useState(false);
  const [cut, setCut] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && clamp && !full) setCut(el.scrollHeight > el.clientHeight + 1);
  }, [bio, clamp, full]);
  return (
    <section className={className}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">About me</h3>
      <p ref={ref} className={clsx('mt-1 whitespace-pre-wrap text-sm text-fg [overflow-wrap:anywhere]', clamp && !full && 'line-clamp-3')}>
        <LinkifiedText text={bio} />
      </p>
      {clamp && cut && (
        <button type="button" className="mt-1 text-xs font-semibold text-fg hover:underline" aria-expanded={full} onClick={() => setFull((f) => !f)}>
          {full ? 'Show Less' : 'View Full Bio'}
        </button>
      )}
    </section>
  );
}

/** Everything inside a profile card: banner, avatar, actions, about, roles, mutuals. */
function ProfileContent({ userId, spaceId, variant, close }: { userId: string; spaceId: string | null; variant: Variant; close: () => void }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const user = useStore((s) => s.users[userId] ?? profile?.user);
  const status = useStore((s) => s.presences[userId] ?? 'offline');
  const me = useStore((s) => s.me);
  const rel = useStore((s) => s.relationships[userId]?.type ?? null);
  const member = useStore((s) => (spaceId ? (s.members[spaceId]?.[userId] ?? (s.myMembers[spaceId]?.user.id === userId ? s.myMembers[spaceId] : undefined)) : undefined)) ?? profile?.member ?? null;
  const space = useStore((s) => (spaceId ? s.spaces[spaceId] : undefined));
  const spaces = useStore((s) => s.spaces);

  useEffect(() => {
    setProfile(null);
    setError(null);
    http
      .get<UserProfile>(`/users/${userId}${spaceId ? `?spaceId=${spaceId}` : ''}`)
      .then(setProfile)
      .catch((err) => setError(errorMessage(err)));
  }, [userId, spaceId]);

  const compact = variant !== 'dialog';
  const self = me?.id === userId;
  const state = useStore.getState();
  const canManageRoles = spaceId ? can(spacePermissions(state, spaceId), Permission.MANAGE_ROLES) : false;
  const myTop = spaceId ? myTopRolePosition(state, spaceId) : 0;
  const roles = space && member ? space.roles.filter((r) => member.roleIds.includes(r.id)).sort((a, b) => b.position - a.position) : [];
  // A bot's own role stays with that bot.
  const assignable = space && member && canManageRoles ? space.roles.filter((r) => !r.isDefault && !r.botId && !member.roleIds.includes(r.id) && r.position < myTop) : [];
  const setRoles = (roleIds: string[]) => void attempt(() => http.patch(`/spaces/${spaceId}/members/${userId}`, { roleIds }));
  const timedOut = member?.timeoutUntil && new Date(member.timeoutUntil).getTime() > Date.now();

  const sendFriendRequest = () => void attempt(() => http.put(`/relationships/${userId}`, { type: 'friend' }), rel === 'incoming' ? 'Friend request accepted.' : 'Friend request sent.');
  const message = () =>
    void openDm(userId).then((c) => {
      if (!c) return;
      close();
      navigate(`/app/@me/${c.id}`);
    });
  const moreEntries = userMenu(userId, spaceId).filter((e) => e.label !== 'Profile' && e.label !== 'Message');
  const showActions = !!user && !self && !user.deleted;
  // Bots can't be messaged or befriended; only the menu (block, report, moderation) applies.
  const isBot = !!user?.bot;

  let actions: ReactNode = null;
  if (showActions && compact) {
    actions = (
      <div className="absolute right-3 top-3 flex gap-2">
        {isBot ? null : rel === 'friend' ? (
          <BannerAction icon={UserCheck} label="Friends" disabled />
        ) : rel === 'outgoing' ? (
          <BannerAction icon={Clock} label="Friend request sent" disabled />
        ) : rel !== 'blocked' ? (
          <BannerAction icon={UserPlus} label={rel === 'incoming' ? 'Accept friend request' : 'Add friend'} onClick={sendFriendRequest} />
        ) : null}
        {variant !== 'panel' && !isBot && <BannerAction icon={MessageSquare} label="Message" onClick={message} />}
        <DropdownMenu
          entries={moreEntries}
          trigger={
            <button type="button" aria-label="More" className="flex size-8 items-center justify-center rounded-full bg-black/45 text-white transition-colors hover:bg-black/65">
              <MoreHorizontal className="size-4" />
            </button>
          }
        />
      </div>
    );
  } else if (showActions) {
    actions = (
      <div className="flex gap-2">
        {isBot ? null : rel === 'friend' ? (
          <Button size="sm" variant="secondary" icon={UserCheck} disabled>
            Friends
          </Button>
        ) : rel === 'outgoing' ? (
          <Button size="sm" variant="secondary" disabled>
            Request sent
          </Button>
        ) : rel !== 'blocked' ? (
          <Button size="sm" variant="secondary" icon={UserPlus} onClick={sendFriendRequest}>
            {rel === 'incoming' ? 'Accept request' : 'Add friend'}
          </Button>
        ) : null}
        {!isBot && (
          <Button size="sm" icon={MessageSquare} onClick={message}>
            Message
          </Button>
        )}
        <DropdownMenu entries={moreEntries} trigger={<button type="button" aria-label="More" className="flex size-8 items-center justify-center rounded-md bg-active text-fg hover:bg-line-strong"><MoreHorizontal className="size-4" /></button>} />
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        <ProfileBanner user={user} className={user?.bannerUrl ? (compact ? 'h-[100px]' : 'h-32') : compact ? 'h-[88px]' : 'h-24'} />
        {compact && actions}
      </div>
      {error ? (
        <p className="p-6 text-sm text-danger">{error}</p>
      ) : !user ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : (
        <div className={compact ? 'px-4 pb-4' : 'px-5 pb-5'}>
          <div className={clsx('flex items-end justify-between', compact ? '-mt-9' : '-mt-11')}>
            <span className="rounded-full ring-[6px] ring-sidebar">
              <Avatar user={user} size={compact ? 72 : 84} status={user.deleted ? null : status} ringClass="ring-sidebar" />
            </span>
            {!compact && actions}
          </div>

          <div className={clsx('mt-3 rounded-xl bg-main', compact ? 'p-3' : 'p-4')}>
            <ProfileName user={user} name={member?.nickname} owner={space?.ownerId === userId} />
            {!user.deleted && <p className="mt-1 text-xs text-faint">{statusLabel[status]}</p>}
            {timedOut && <p className="mt-2 rounded bg-danger-soft px-2 py-1 text-xs text-danger">Timed out for {relativeFuture(member!.timeoutUntil!)}</p>}

            {user.bio && <AboutMe bio={user.bio} className="mt-4" />}

            <section className="mt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Member since</h3>
              <p className="mt-1 text-sm text-fg">
                Spawnwire: {new Date(user.createdAt).toLocaleDateString()}
                {member && ` · ${space?.name}: ${new Date(member.joinedAt).toLocaleDateString()}`}
              </p>
            </section>

            {space && member && (
              <section className="mt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Roles</h3>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {roles.filter((r) => !r.isDefault).map((r) => (
                    <span key={r.id} className="inline-flex items-center gap-1.5 rounded-md bg-active px-2 py-0.5 text-xs text-fg">
                      <span className="size-2.5 rounded-full" style={{ background: r.color ?? 'var(--p-faint)' }} aria-hidden />
                      {r.name}
                      {canManageRoles && r.position < myTop && !r.botId && (
                        <button type="button" aria-label={`Remove role ${r.name}`} className="text-faint hover:text-danger" onClick={() => setRoles(member.roleIds.filter((id) => id !== r.id))}>
                          <X className="size-3" />
                        </button>
                      )}
                    </span>
                  ))}
                  {roles.filter((r) => !r.isDefault).length === 0 && <span className="text-sm text-muted">No roles</span>}
                  {assignable.length > 0 && (
                    <DropdownMenu
                      align="start"
                      entries={assignable.map((r) => ({ label: r.name, onSelect: () => setRoles([...member.roleIds, r.id]) }))}
                      trigger={
                        <button type="button" aria-label="Add role" className="inline-flex size-6 items-center justify-center rounded-md bg-active text-muted hover:text-fg">
                          <Plus className="size-3.5" />
                        </button>
                      }
                    />
                  )}
                </div>
              </section>
            )}

            {profile && (profile.mutualSpaceIds.length > 0 || profile.mutualFriendIds.length > 0) && (
              <section className="mt-4 space-y-2">
                {profile.mutualSpaceIds.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Mutual spaces</h3>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {profile.mutualSpaceIds.map((id) =>
                        spaces[id] ? (
                          <button
                            key={id}
                            type="button"
                            className="flex items-center gap-2 rounded-md bg-active px-2 py-1 text-xs text-fg hover:bg-line-strong"
                            onClick={() => {
                              close();
                              navigate(`/app/${id}`);
                            }}
                          >
                            <SpaceIcon name={spaces[id]!.name} iconUrl={spaces[id]!.iconUrl} size={18} />
                            {spaces[id]!.name}
                          </button>
                        ) : null,
                      )}
                    </div>
                  </div>
                )}
                {profile.mutualFriendIds.length > 0 && (
                  <p className="text-xs text-muted">
                    {profile.mutualFriendIds.length} mutual friend{profile.mutualFriendIds.length === 1 ? '' : 's'}
                  </p>
                )}
              </section>
            )}
            {self && (
              <Button className="mt-4 w-full" variant="secondary" onClick={() => ui.open({ kind: 'userSettings', section: 'profile' })}>
                Edit profile
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const POPOUT_GAP = 12;
const VIEWPORT_PAD = 8;

/** Keeps a fixed-position popout beside `anchor`, flipping to the left and staying on screen as it grows. */
function usePopoutPosition(anchor: ProfileAnchor | undefined) {
  // A callback ref: the dialog portal mounts its content a render later than this component.
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!el || !anchor) return;
    const place = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      let left = anchor.right + POPOUT_GAP;
      if (left + w > window.innerWidth - VIEWPORT_PAD) left = anchor.left - w - POPOUT_GAP;
      left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - w - VIEWPORT_PAD));
      const top = Math.max(VIEWPORT_PAD, Math.min(anchor.top, window.innerHeight - h - VIEWPORT_PAD));
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el, anchor]);
  return setEl;
}

/** A profile opened from a click: pops out beside what was clicked on desktop, centred otherwise. */
export function ProfileDialog({ userId, spaceId, anchor }: { userId: string; spaceId: string | null; anchor?: ProfileAnchor }) {
  const isMobile = useIsMobile();
  const popout = !!anchor && !isMobile;
  const ref = usePopoutPosition(popout ? anchor : undefined);
  const user = useStore((s) => s.users[userId]);

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => !o && ui.close()}>
      <DialogPrimitive.Portal>
        {/* A popout closes on any click outside it, so its overlay is invisible. */}
        <DialogPrimitive.Overlay className={clsx('fixed inset-0 z-[60]', popout ? 'bg-transparent' : 'animate-fade bg-black/60')} />
        <DialogPrimitive.Content
          ref={ref}
          aria-describedby={undefined}
          className={clsx(
            'animate-pop fixed z-[61] overflow-hidden overflow-y-auto border border-line bg-sidebar shadow-pop outline-none',
            popout
              ? 'scroll-thin max-h-[calc(100vh-16px)] w-[300px] rounded-xl'
              : 'left-1/2 top-1/2 max-h-[90vh] w-[calc(100vw-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl',
          )}
        >
          <DialogPrimitive.Title className="sr-only">{user?.displayName ?? 'Profile'}</DialogPrimitive.Title>
          <ProfileContent userId={userId} spaceId={spaceId} variant={popout ? 'popout' : 'dialog'} close={ui.close} />
          {!popout && (
            <div className="absolute right-3 top-3">
              <IconButton icon={X} label="Close" className="bg-black/30 text-white hover:bg-black/50" onClick={ui.close} />
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** The other person's profile, shown beside a one-to-one conversation. */
export function ProfilePanel({ userId }: { userId: string }) {
  return (
    <div className="scroll-thin flex min-h-0 flex-1 flex-col overflow-y-auto bg-sidebar">
      <ProfileContent userId={userId} spaceId={null} variant="panel" close={() => {}} />
      <div className="mt-auto p-4 pt-0">
        <Button className="w-full" variant="secondary" onClick={() => ui.open({ kind: 'profile', userId, spaceId: null })}>
          View Full Profile
        </Button>
      </div>
    </div>
  );
}
